import React from 'react';

interface ScreenLayoutProps {
  children: React.ReactNode;
  enableMobileContainer?: boolean;
}

/**
 * ScreenLayout - Wrapper component for consistent screen layout
 *
 * Provides a consistent layout structure with optional mobile container styling.
 * This component centralizes the repeated mobile container pattern used across screens.
 *
 * @param children - The content to render within the layout
 * @param enableMobileContainer - Whether to apply mobile container styling (default: true)
 *
 * @example
 * ```tsx
 * <ScreenLayout enableMobileContainer={ENABLE_MOBILE_CONTAINER}>
 *   <WelcomeScreen />
 * </ScreenLayout>
 * ```
 */
export const ScreenLayout: React.FC<ScreenLayoutProps> = ({
  children,
  enableMobileContainer = true,
}) => {
  return (
    <div className="max-w-[1000px] mx-auto">
      <div
        className={
          enableMobileContainer
            ? 'min-h-screen bg-gray-100 flex items-center justify-center p-4'
            : ''
        }
      >
        <div
          className={
            enableMobileContainer
              ? 'w-full max-w-sm h-screen max-h-[800px] bg-background rounded-2xl shadow-2xl overflow-hidden relative'
              : ''
          }
        >
          {children}
        </div>
      </div>
    </div>
  );
};
